import dayjs from "dayjs";
import { US_STATES, FIELD_MAX_LENGTHS } from "./constants";

const VALID_STATE_CODES = new Set(US_STATES.map((s) => s.code));

// Allow accented chars and apostrophes (O'Brien, Mary-Jane, etc.)
const NAME_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/;
const CITY_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'.-]+$/;
const STREET_RE = /^[A-Za-z0-9\s,.#/-]+$/;
const UNIT_RE = /^[A-Za-z0-9\s#.-]+$/;
// 12345 or 12345-6789
const ZIP_RE = /^\d{5}(-\d{4})?$/;

// Strip null/control characters and common injection chars; keep apostrophes, quotes, etc.
export function sanitize(str) {
  if (typeof str !== "string") return str;
  // eslint-disable-next-line no-control-regex
  return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F<>{}$;\\]/g, "").slice(0, 200);
}

export function trimAll(obj) {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    out[k] = typeof v === "string" ? sanitize(v.trim()) : v;
  });
  return out;
}

export function normalizeIncoming(payload, fallback) {
  if (!payload) return { ...fallback };
  return Object.keys(fallback).reduce((acc, key) => {
    acc[key] =
      typeof payload[key] === "string"
        ? sanitize(payload[key])
        : (payload[key] ?? fallback[key]);
    return acc;
  }, {});
}

export function validateAll(data) {
  const errors = {};

  const required = [
    "firstName",
    "lastName",
    "phoneNumber",
    "birthday",
    "streetAddress",
    "city",
    "state",
    "zipCode",
  ];
  for (const f of required) {
    if (!data[f]) errors[f] = "Required";
  }

  if (data.firstName) {
    if (data.firstName.length > FIELD_MAX_LENGTHS.firstName)
      errors.firstName = `Max ${FIELD_MAX_LENGTHS.firstName} characters`;
    else if (!NAME_RE.test(data.firstName))
      errors.firstName = "Letters, spaces, hyphens, and apostrophes only";
  }

  if (data.lastName) {
    if (data.lastName.length > FIELD_MAX_LENGTHS.lastName)
      errors.lastName = `Max ${FIELD_MAX_LENGTHS.lastName} characters`;
    else if (!NAME_RE.test(data.lastName))
      errors.lastName = "Letters, spaces, hyphens, and apostrophes only";
  }

  if (data.phoneNumber && !isValidNanp(data.phoneNumber))
    errors.phoneNumber = "Invalid phone number";

  if (data.birthday) {
    const dob = dayjs(data.birthday);
    const now = dayjs();
    if (!dob.isValid()) {
      errors.birthday = "Invalid date";
    } else if (dob.isAfter(now)) {
      errors.birthday = "Date of birth cannot be in the future";
    } else if (now.diff(dob, "year") < 18) {
      errors.birthday = "Must be at least 18 years old";
    } else if (dob.year() < 1900) {
      errors.birthday = "Invalid date of birth";
    }
  }

  if (data.streetAddress) {
    if (data.streetAddress.length > FIELD_MAX_LENGTHS.streetAddress)
      errors.streetAddress = `Max ${FIELD_MAX_LENGTHS.streetAddress} characters`;
    else if (!STREET_RE.test(data.streetAddress))
      errors.streetAddress = "Invalid characters in street address";
  }

  if (data.unitNumber) {
    if (data.unitNumber.length > FIELD_MAX_LENGTHS.unitNumber)
      errors.unitNumber = `Max ${FIELD_MAX_LENGTHS.unitNumber} characters`;
    else if (!UNIT_RE.test(data.unitNumber))
      errors.unitNumber = "Invalid characters";
  }

  if (data.city) {
    if (data.city.length > FIELD_MAX_LENGTHS.city)
      errors.city = `Max ${FIELD_MAX_LENGTHS.city} characters`;
    else if (!CITY_RE.test(data.city))
      errors.city = "Letters, spaces, hyphens, and periods only";
  }

  if (data.state && !VALID_STATE_CODES.has(data.state))
    errors.state = "Please select a valid US state";

  if (data.zipCode && !ZIP_RE.test(data.zipCode))
    errors.zipCode = "5 digits, or ZIP+4 format (e.g. 12345-6789)";

  return { ok: Object.keys(errors).length === 0, errors };
}

export function digitsOnly(s) {
  return (s || "").replace(/\D+/g, "");
}

export function normalizePhone(input) {
  const d = digitsOnly(input);
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1"))
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return input;
}

export function isValidNanp(s) {
  const d = digitsOnly(s);
  return d.length === 10 || (d.length === 11 && d.startsWith("1"));
}

export function safeParse(t) {
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}
